import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

export default function LongDivisionCalc() {
  const [dividend, setDividend] = useState(1234);
  const [divisor, setDivisor] = useState(7);
  const [showWork, setShowWork] = useState(false);
  const c = useMemo(() => {
    if (divisor === 0) return null;
    const q = Math.floor(dividend / divisor);
    const r = dividend - q*divisor;
    // steps
    const digits = String(Math.abs(dividend)).split('');
    const steps = [];
    let bring = 0;
    for (let i = 0; i < digits.length; i++) {
      bring = bring * 10 + parseInt(digits[i]);
      const d = Math.floor(bring / divisor);
      const rem = bring - d*divisor;
      steps.push({ current: bring, digit: d, subtract: d*divisor, remainder: rem });
      bring = rem;
    }
    return { q, r, steps };
  }, [dividend, divisor]);
  return (
    <CalcShell look="cnet" title="Long Division Calculator" subtitle="Perform long division showing quotient, remainder, and each step.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Dividend</label><input type="number" value={dividend} onChange={e => setDividend(+e.target.value || 0)} /></div>
          <div className="field"><label>Divisor</label><input type="number" value={divisor} onChange={e => setDivisor(+e.target.value || 0)} /></div>
        </div>
        <div>
          {c && <div className="result-panel"><div className="result-title">{dividend} ÷ {divisor}</div><div className="result-value">{c.q} <span className="text-lg">R {c.r}</span></div></div>}
        </div>
      </div>
      {c && (
        <div className="mt-6">
          <h3 className="font-bold text-slate-900 mb-2">Long Division Steps</h3>
          <div className="overflow-auto">
            <table className="data-table"><thead><tr><th>Step</th><th>Bring down</th><th>Quotient digit</th><th>Subtract</th><th>Remainder</th></tr></thead><tbody>
              {c.steps.map((s, i) => <tr key={i}><td>{i + 1}</td><td>{s.current}</td><td>{s.digit}</td><td>{s.subtract}</td><td>{s.remainder}</td></tr>)}
            </tbody></table>
          </div>
          <div className="mt-4">
            <button onClick={() => setShowWork(v => !v)} data-testid="ld-show-work-toggle"
              className="text-sm px-4 py-1.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium">
              {showWork ? '− Hide working' : '+ Show working'}
            </button>
            {showWork && (
              <div className="mt-4 p-5 bg-blue-50 border border-blue-100 rounded-xl" data-testid="ld-work-panel">
                <h3 className="text-lg font-bold text-slate-900 mb-3">Step-by-step working</h3>
                <ol className="list-decimal ml-5 space-y-2 text-slate-800 text-[15px] leading-7">
                  <li><b>Set up</b>: dividend ({dividend}) ÷ divisor ({divisor}).</li>
                  <li><b>Bring down</b> each digit of the dividend one at a time to form a running number.</li>
                  <li><b>Divide</b> the running number by the divisor to get the next digit of the quotient.</li>
                  <li><b>Multiply</b> that quotient digit by the divisor and <b>subtract</b> to get the remainder for that step.</li>
                  <li><b>Repeat</b> until every digit is brought down. The final subtraction gives the overall remainder.</li>
                  <li><b>Result</b>: quotient = <b className="text-[#4c7b25]">{c.q}</b>, remainder = <b className="text-[#4c7b25]">{c.r}</b>. Verify: {c.q} × {divisor} + {c.r} = {c.q * divisor + c.r}.</li>
                </ol>
              </div>
            )}
          </div>
        </div>
      )}
    </CalcShell>
  );
}
